import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useState } from "react";

import ImportedRangeSlider from "react-range-slider-input";
import "react-range-slider-input/dist/style.css";

interface IRangeSliderProps {
  value: { min: number; max: number };
  onChange: (value: { min: number; max: number }) => void;
  label: string;
  sliderMin: number;
  sliderMax: number;
  step?: number;
  isDecimal?: boolean;
}

const RangeSlider = ({
  value,
  onChange,
  label,
  sliderMin,
  sliderMax,
  step = 1,
  isDecimal = false,
}: IRangeSliderProps) => {
  const [currentMinMax, setCurrentMinMax] = useState(value);

  const [inputMinStr, setInputMinStr] = useState(String(value.min));
  const [inputMaxStr, setInputMaxStr] = useState(String(value.max));

  const [isDragging, setIsDragging] = useState(false);

  const getStepPrecision = useCallback((): number => {
    if (!isDecimal || !step || step === 0 || Math.floor(step) === step)
      return 0;
    const parts = String(step).split(".");
    return parts[1] ? parts[1].length : 0;
  }, [isDecimal, step]);
  const precision = useMemo(getStepPrecision, [getStepPrecision]);

  const debouncedParentOnChange = useMemo(
    () =>
      debounce((newValue: { min: number; max: number }) => {
        onChange(newValue);
      }, 300),
    [onChange]
  );

  useEffect(() => {
    if (!isDragging) {
      let newMin = Math.max(sliderMin, Math.min(value.min, sliderMax));
      let newMax = Math.min(sliderMax, Math.max(value.max, sliderMin));

      if (newMin > newMax) {
        newMax = newMin;
      }

      const validParentValue = { min: newMin, max: newMax };

      setCurrentMinMax(validParentValue);
      setInputMinStr(validParentValue.min.toFixed(precision));
      setInputMaxStr(validParentValue.max.toFixed(precision));
    }
  }, [value, sliderMin, sliderMax, isDragging, precision]);

  useEffect(() => {
    return () => {
      debouncedParentOnChange.cancel();
    };
  }, [debouncedParentOnChange]);

  const handleSliderInput = (newRange: number[]) => {
    const newMinValue =
      typeof newRange[0] === "number" ? newRange[0] : currentMinMax.min;
    const newMaxValue =
      typeof newRange[1] === "number" ? newRange[1] : currentMinMax.max;

    const newValues = { min: newMinValue, max: newMaxValue };

    setCurrentMinMax(newValues);
    setInputMinStr(newValues.min.toFixed(precision));
    setInputMaxStr(newValues.max.toFixed(precision));

    if (isDragging) {
      debouncedParentOnChange(newValues);
    } else {
      onChange(newValues);
    }
  };

  const handleSliderDragStart = () => {
    setIsDragging(true);
    debouncedParentOnChange.cancel();
  };

  const handleSliderDragEnd = () => {
    setIsDragging(false);
    onChange(currentMinMax);
  };

  const handleNumericInputChange = (
    inputValueStr: string,
    inputType: "min" | "max"
  ) => {
    if (inputType === "min") {
      setInputMinStr(inputValueStr);
    } else {
      setInputMaxStr(inputValueStr);
    }
  };

  const commitInput = (inputType: "min" | "max") => {
    const rawValueStr = inputType === "min" ? inputMinStr : inputMaxStr;
    let numericValue = parseFloat(rawValueStr);

    if (isNaN(numericValue)) {
      setInputMinStr(currentMinMax.min.toFixed(precision));
      setInputMaxStr(currentMinMax.max.toFixed(precision));
      return;
    }

    numericValue = Math.max(sliderMin, Math.min(sliderMax, numericValue));

    if (isDecimal && step && step > 0) {
      numericValue = Math.round(numericValue / step) * step;
      numericValue = parseFloat(numericValue.toFixed(precision));
    } else if (!isDecimal) {
      numericValue = Math.round(numericValue);
    }

    let newMin = currentMinMax.min;
    let newMax = currentMinMax.max;

    if (inputType === "min") {
      newMin = numericValue;
      if (newMin > newMax) {
        newMax = newMin;
      }
    } else {
      newMax = numericValue;
      if (newMax < newMin) {
        newMin = newMax;
      }
    }

    newMin = Math.max(sliderMin, Math.min(newMin, sliderMax));
    newMax = Math.min(sliderMax, Math.max(newMax, sliderMin));
    if (newMin > newMax) {
      if (inputType === "min") newMax = newMin;
      else newMin = newMax;
    }

    const newValues = { min: newMin, max: newMax };

    setCurrentMinMax(newValues);
    setInputMinStr(newValues.min.toFixed(precision));
    setInputMaxStr(newValues.max.toFixed(precision));
    onChange(newValues);
  };

  const sliderColor = "#1e40af";
  const inactiveTrackColor = "#e9ecef";

  const customSliderStyles = `
    .range-slider {
      background-color: ${inactiveTrackColor} !important; 
      height: 6px !important; 
      border-radius: 3px !important; 
    }

    .range-slider .range-slider__range {
      background-color: ${sliderColor} !important;
    }

    .range-slider .range-slider__thumb {
      background-color: ${sliderColor} !important;
      border: 1px solid ${sliderColor} !important;
      width: 18px;
      height: 18px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }

    .range-slider .range-slider__thumb:focus-visible {
      box-shadow: 0 0 0 5px ${sliderColor}66 !important; 
    }
  `;

  return (
    <div className="px-2">
      <style>{customSliderStyles}</style>
      {label && (
        <div className="mb-2 text-sm font-medium text-center text-gray-700">
          {label}
        </div>
      )}
      <div className="p-2">
        <div className="flex justify-center gap-2 pb-2">
          <input
            type="number"
            step={isDecimal && step ? step : 1}
            value={inputMinStr}
            onChange={(e) => handleNumericInputChange(e.target.value, "min")}
            onBlur={() => commitInput("min")}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                (e.target as HTMLInputElement).blur();
              }
            }}
            className="w-20 p-2 text-xs text-black border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            aria-label={`${label} minimum value`}
          />
          <input
            type="number"
            step={isDecimal && step ? step : 1}
            value={inputMaxStr}
            onChange={(e) => handleNumericInputChange(e.target.value, "max")}
            onBlur={() => commitInput("max")}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                (e.target as HTMLInputElement).blur();
              }
            }}
            className="w-20 p-2 text-xs text-black border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            aria-label={`${label} maximum value`}
          />
        </div>
        <ImportedRangeSlider
          min={sliderMin}
          max={sliderMax}
          step={step}
          value={[currentMinMax.min, currentMinMax.max]}
          onInput={handleSliderInput}
          onThumbDragStart={handleSliderDragStart}
          onThumbDragEnd={handleSliderDragEnd}
        />

        <div className="flex justify-between px-1 pt-2 mt-1 text-xs text-gray-500">
          <span>{sliderMin.toFixed(isDecimal ? precision : 0)}</span>
          <span>{sliderMax.toFixed(isDecimal ? precision : 0)}</span>
        </div>
      </div>
    </div>
  );
};

export default RangeSlider;
